import React, { createContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { HubConnectionBuilder, LogLevel, HttpTransportType } from "@microsoft/signalr";
import apiService, { getSignalRToken, setSignalRToken , FILES_BASE_URL, odswiezSesjeDlaHuba } from "../api/apiService";
import { useAuth } from "../hooks/useAuth";
import { tozsamoscKonta } from "../hooks/usePamiecPowloki";
import { translatePath } from "../utils/routeHelpers";
// [A15-07] Adresy powiadomień HR liczymy z mapy tras, zamiast wpisywać je wprost.
import { localizedPath } from "../hooks/useLocalizedNavigate";
import { KATEGORIA_AKCEPTACJE, KODY_AKCEPTACJI } from "../constants/notificationCategories";
import { ZDARZENIA_SYNC, zastosujSync, linkWskazujeObiekt, czekaNaDecyzjeWOdczycie } from "../hooks/useSynchronizacjaPowiadomien";

/* 🔴 PUSTY ŁAŃCUCH, NIE `undefined`. `FILES_BASE_URL` liczy się z `REACT_APP_API_URL`; gdy tej
   zmiennej nie ma przy budowaniu, gołe sklejanie dało adres **`"undefined/hubs/notifications"`** —
   napis, który SignalR odrzuca wyjątkiem „Cannot resolve 'undefined/hubs/notifications'" zamiast
   po prostu się nie połączyć. Zmierzone 2026-09-20 w pełnym przebiegu suity frontu: wyjątek leci
   z EFEKTU, czyli wywraca całe drzewo Reacta, a nie tylko powiadomienia.
   Pusta baza znaczy „ten sam adres, co strona" — to jedyne sensowne znaczenie braku bazy
   i dokładnie to, czego przeglądarka użyje dla adresu względnego. Na produkcji zmienna JEST
   ustawiona, więc tam nic się nie zmienia. */
const HUB_URL = (FILES_BASE_URL || "") + "/hubs/notifications";

/** [KA-12 2026-09-26] Sekundy do wygaśnięcia tokenu JWT (claim exp) albo null, gdy nie da się go odczytać. */
export const sekundyDoWygasniecia = (token, terazMs = Date.now()) => {
    try {
        const czesc = String(token || "").split(".")[1];
        if (!czesc) return null;
        const json = JSON.parse(atob(czesc.replace(/-/g, "+").replace(/_/g, "/")));
        return typeof json.exp === "number" ? json.exp - Math.floor(terazMs / 1000) : null;
    } catch {
        return null;
    }
};

/** Token nadaje się dla huba: jest i wygasa za więcej niż minutę (albo nie ma czytelnego `exp` — nie ruszamy go). */
const tokenWystarczy = (token) => {
    if (!token) return false;
    const zostalo = sekundyDoWygasniecia(token);
    return zostalo === null || zostalo > 60;
};

/* [P5-06, przegląd poprawek KA-12 2026-09-26] ODRZUCONY TOKEN HUBA.
   BYŁO ŹLE: token dla huba oceniany był wyłącznie po `exp`. Wylogowanie, „wyloguj wszędzie”, replay albo zmiana hasła na
   INNYM urządzeniu rotują stempel i zrywają huby wszystkich kart konta — a ta karta łączyła się ponownie tokenem jeszcze
   ważnym, ale z nieważnym stemplem. Cała sekwencja withAutomaticReconnect (~47 s) kończyła się 401 i „backend-network-error”,
   a powiadomienia wracały dopiero po własnym restarcie (/users/me → 401 → refresh), czyli po około minucie.
   NAPRAWA: nieudana próba ponownego połączenia (politykaPonownegoPolaczenia) oznacza BIEŻĄCY token jako odrzucony — następna
   negocjacja idzie po ROTACJI sesji (/users/refresh). Jeśli w międzyczasie ktoś już odświeżył sesję (przechwytywacz 401 dał
   nowy token), bierzemy nowy token bez drugiej rotacji. Rotacja nieudana — znacznik zostaje, spróbujemy przy kolejnej próbie. */
let wymusRotacjeDlaHuba = false;
let tokenOdrzuconyHuba = null;

/** [P5-06] Bieżący token huba nie przeszedł negocjacji — następne pobranie tokenu idzie przez rotację sesji. */
export const oznaczTokenHubaDoOdswiezenia = () => {
    wymusRotacjeDlaHuba = true;
    tokenOdrzuconyHuba = getSignalRToken() || null;
};

/** [KA-12 → P5-R5] Token dla huba: z pamięci, a gdy wygasa w ciągu 60 s — ROTACJA sesji przez /users/refresh
 *  (`odswiezSesjeDlaHuba`, jedna naraz z przechwytywaczem 401). /users/me od KA-04 NIE przedłuża tokenu (wydaje go z tym
 *  samym terminem), więc służy wyłącznie do pobrania tokenu, gdy w pamięci nie ma żadnego (np. po przeładowaniu karty).
 *  [P5-06] Token oznaczony jako odrzucony (oznaczTokenHubaDoOdswiezenia) — rotacja, nawet gdy `exp` jest daleko. */
export const tokenHubaSwiezy = async () => {
    let kandydat = getSignalRToken();
    if (wymusRotacjeDlaHuba && kandydat && kandydat !== tokenOdrzuconyHuba) {
        // Sesję odświeżył już ktoś inny (przechwytywacz 401) — nowy token ma nowy stempel, druga rotacja zbędna.
        wymusRotacjeDlaHuba = false;
        tokenOdrzuconyHuba = null;
    }
    const wymus = wymusRotacjeDlaHuba;
    if (!wymus && tokenWystarczy(kandydat)) return kandydat;
    if (!wymus && !kandydat) {
        try {
            const res = await apiService.getMe();
            kandydat = (res && res.data && res.data.accessToken) || null;
            if (kandydat) setSignalRToken(kandydat);
        } catch { /* brak sieci / nieważna sesja — niżej rotacja albo to, co mamy */ }
        if (tokenWystarczy(kandydat)) return kandydat;
    }
    try {
        const odswiezony = await odswiezSesjeDlaHuba();
        if (odswiezony) {
            wymusRotacjeDlaHuba = false;
            tokenOdrzuconyHuba = null;
            return odswiezony;
        }
    } catch { /* sesja nieważna (przechwytywacz wyloguje) albo awaria przejściowa — negocjacja dostanie to, co mamy */ }
    return kandydat || getSignalRToken();
};

/** [P5-06] Odstępy prób ponownego połączenia huba (jak dotąd: 0, 2, 5, 10, 30 s, potem własny restart). */
export const ODSTEPY_PONOWNEGO_POLACZENIA_MS = [0, 2000, 5000, 10000, 30000];

/** [P5-06] Polityka ponownego łączenia: te same odstępy, a KAŻDA nieudana próba (previousRetryCount ≥ 1 — np. negocjacja
 *  odrzucona 401 po rotacji stempla na innym urządzeniu) oznacza bieżący token jako odrzucony, więc następna próba idzie już
 *  z tokenem po rotacji sesji. Pierwsza próba (0 ms) zostaje przy tokenie z pamięci — zwykły zanik sieci nie rotuje sesji. */
export const politykaPonownegoPolaczenia = {
    nextRetryDelayInMilliseconds: (kontekst) => {
        const proba = (kontekst && kontekst.previousRetryCount) || 0;
        if (proba >= 1) oznaczTokenHubaDoOdswiezenia();
        return proba < ODSTEPY_PONOWNEGO_POLACZENIA_MS.length ? ODSTEPY_PONOWNEGO_POLACZENIA_MS[proba] : null;
    },
};

/* ── [A09-07] Odstępy własnego restartu huba (po wyczerpaniu withAutomaticReconnect) ──
   Rosnące, ostatni powtarzany w nieskończoność — świadomie WOLNE, żeby padnięty
   backend nie dostał lawiny negocjacji od wszystkich otwartych kart w firmie. */
const RECONNECT_DELAYS_MS = [5000, 15000, 30000, 60000, 120000];
// Minimalny odstęp między próbami wywołanymi powrotem karty na wierzch / powrotem sieci —
// samo przełączanie zakładek nie może zamienić się w odpytywanie serwera co sekundę.
const WAKEUP_THROTTLE_MS = 15000;

// Mapowanie ikon powiadomień na emoji (dla natywnych powiadomień systemowych)
const ICON_EMOJI = {
    FiMessageSquare: "\u{1F4E9}",
    FiRefreshCw: "\u{1F504}",
    FiMail: "\u{2709}\u{FE0F}",
    FiEdit: "\u{270F}\u{FE0F}",
    FiTrello: "\u{1F4CB}",
    FiClock: "\u{23F0}",
    FiBell: "\u{1F514}",
    FiFileText: "\u{1F4C4}",
    FiAward: "\u{1F3C6}",
    FiHardDrive: "\u{1F4BE}",
    FiBriefcase: "\u{1F4BC}",
    FiAlertCircle: "\u{26A0}\u{FE0F}",
};

/* ── [A09-21] Prefiksy kodów powiadomień, dla których emitujemy alias okna 'hr-notification' ──
   To jedyny kanał, którym SignalR ożywia podstrony „Mój Dział" (pulpit, wnioski, plan urlopów,
   praca zdalna). Dokładając nowy rodzaj powiadomienia kadrowego, dopisz tu jego prefiks —
   inaczej strona przełożonego nie odświeży się sama i człowiek zobaczy zmianę dopiero po F5. */
export const PREFIKSY_ALIASU_HR = ["hr_", "remote_", "schedule_", "vacation_"];

export const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    /* 🔴 [OBSZAR 20, zgłoszenie właściciela 2026-09-18] „w headerze mam licznik ze 5, odczytuję je, a na komputerze
       znowu mam 5." Licznik liczył WSZYSTKIE nieprzeczytane, a dzwonek pokazywał 20 najnowszych zawężonych do 7 dni —
       starsze nieprzeczytane były liczone i niewidoczne, więc nie dało się ich odczytać. Osobne stronicowanie
       NIEPRZECZYTANYCH (bez zawężenia po dacie) dociąga do jednej listy wszystko, co liczy licznik; liczba i pozycje
       przychodzą z JEDNEGO zapytania (`{ items, unreadTotal }`). */
    const [unreadPage, setUnreadPage] = useState(1);
    const [unreadHasMore, setUnreadHasMore] = useState(false);
    // Flaga per USER z backendu (User.WindowsNotificationsEnabled) — localStorage jest tylko
    // cache'em na pierwszy render (wcześniej flaga żyła wyłącznie per przeglądarka i ginęła
    // na każdym nowym stanowisku).
    const [desktopEnabled, setDesktopEnabled] = useState(() => {
        return localStorage.getItem("desktopNotifications") !== "false";
    });
    const [desktopPermission, setDesktopPermission] = useState(
        typeof Notification !== "undefined" ? Notification.permission : "denied"
    );
    const connectionRef = useRef(null);
    // [A09-07] Stan własnego restartu huba (timer + numer próby + znacznik ostatniej próby).
    const restartTimerRef = useRef(null);
    const restartAttemptRef = useRef(0);
    const lastAttemptAtRef = useRef(0);
    const toastCallbackRef = useRef(null);
    const announcementCallbackRef = useRef(null);
    /* ⚠️ [2026-09-08] ZBIÓR, NIE JEDNO GNIAZDO.
       BYŁO źLE: `useRef(null)` + `badgeCallbackRef.current = callback` — jedno gniazdo. Kto zarejestrował
       się jako drugi, CICHO wypychał pierwszego; kto pierwszy, cicho nie dostawał nic po drugim. Dopóki
       konsument był jeden (menu boczne), nikt tego nie widział. Od dziś kropki konsumuje też pasek
       zakładek „Mojego Działu" — przy jednym gnieździe jeden z dwóch po prostu przestawałby się
       odświeżać, i to bez żadnego błędu w konsoli. */
    const badgeCallbacksRef = useRef(new Set());
    const termsCallbackRef = useRef(null);
    /* 🔴 [zgłoszenie właściciela 2026-09-17] OSOBNE gniazda dla „przeczytane gdzie indziej".
       Nie wolno ich mieszać z `announcementCallbackRef` / `termsCallbackRef`: tamte znaczą
       „pojawiło się coś NOWEGO" i mogą (jak dziś) wymusić pokazanie okna. Tutaj znaczenie jest
       odwrotne — „to już załatwione na innym urządzeniu, ZDEJMIJ to z ekranu". Jedno gniazdo na
       oba znaczenia kończy się oknem, które samo się otwiera po zamknięciu go gdzie indziej. */
    const announcementReadCallbackRef = useRef(null);
    const termsAcceptedCallbackRef = useRef(null);
    const desktopEnabledRef = useRef(desktopEnabled);
    const isOpenRef = useRef(isOpen);

    // Rejestracja callback dla toast (żeby nie mieć circular dependency)
    const registerToastCallback = useCallback((callback) => {
        toastCallbackRef.current = callback;
    }, []);

    // Rejestracja callback dla komunikatów (announcement alert → Layout wymusza modal)
    const registerAnnouncementCallback = useCallback((callback) => {
        announcementCallbackRef.current = callback;
    }, []);

    // Rejestracja callback dla kropek (menu boczne + paski zakładek). Zwraca funkcję WYREJESTROWANIA —
    // bez niej odmontowany konsument zostawałby w zbiorze na zawsze i dostałby push do martwego stanu.
    const registerBadgeCallback = useCallback((callback) => {
        if (typeof callback !== "function") return () => {};
        badgeCallbacksRef.current.add(callback);
        return () => { badgeCallbacksRef.current.delete(callback); };
    }, []);

    /** Rozesłanie do WSZYSTKICH konsumentów kropek. Błąd jednego nie może uciszyć pozostałych. */
    const powiadomOKropce = useCallback((n) => {
        badgeCallbacksRef.current.forEach((cb) => {
            try { cb(n); } catch { /* kropka jest wygodą — cisza jest tu właściwą reakcją */ }
        });
    }, []);

    // Rejestracja callback dla regulaminu (terms update)
    const registerTermsCallback = useCallback((callback) => {
        termsCallbackRef.current = callback;
    }, []);

    // Komunikat zamknięty na INNYM urządzeniu tego samego konta — zdejmij go tutaj.
    const registerAnnouncementReadCallback = useCallback((callback) => {
        announcementReadCallbackRef.current = callback;
    }, []);

    // Regulamin przyjęty na INNYM urządzeniu tego samego konta — zdejmij bramkę tutaj.
    const registerTermsAcceptedCallback = useCallback((callback) => {
        termsAcceptedCallbackRef.current = callback;
    }, []);

    // Sync refs
    useEffect(() => { desktopEnabledRef.current = desktopEnabled; }, [desktopEnabled]);
    useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

    // --- Powiadomienia systemowe Windows (Web Notification API) ---

    // Poproś o pozwolenie na powiadomienia desktopowe
    const requestDesktopPermission = useCallback(async () => {
        if (typeof Notification === "undefined") return "denied";
        if (Notification.permission === "granted") {
            setDesktopPermission("granted");
            return "granted";
        }
        if (Notification.permission === "denied") {
            setDesktopPermission("denied");
            return "denied";
        }
        try {
            const result = await Notification.requestPermission();
            setDesktopPermission(result);
            return result;
        } catch {
            return "denied";
        }
    }, []);

    // Włącz/wyłącz powiadomienia desktopowe — zapis per USER na backendzie (kanał windows),
    // żeby wybór obowiązywał na każdym stanowisku, nie tylko w tej przeglądarce.
    const persistDesktopChannel = useCallback(async (enabled) => {
        try {
            const res = await apiService.getNotificationChannel();
            const ch = res?.data || { appEnabled: true, emailEnabled: true };
            await apiService.updateNotificationChannel(ch.appEnabled !== false, ch.emailEnabled !== false, enabled);
        } catch { /* best-effort — lokalnie i tak działa */ }
    }, []);

    const toggleDesktopNotifications = useCallback(async () => {
        if (desktopEnabled) {
            setDesktopEnabled(false);
            localStorage.setItem("desktopNotifications", "false");
            persistDesktopChannel(false);
        } else {
            const permission = await requestDesktopPermission();
            if (permission === "granted") {
                setDesktopEnabled(true);
                localStorage.setItem("desktopNotifications", "true");
                persistDesktopChannel(true);
            }
        }
    }, [desktopEnabled, requestDesktopPermission, persistDesktopChannel]);

    // Wyślij natywne powiadomienie systemowe (używamy refów żeby nie restartować SignalR)
    const showDesktopNotification = useCallback((notification) => {
        if (!desktopEnabledRef.current) return;
        if (typeof Notification === "undefined") return;
        if (Notification.permission !== "granted") return;

        // Nie wysyłaj jeśli okno jest aktywne (focused) i panel jest otwarty
        if (document.hasFocus() && isOpenRef.current) return;

        const emoji = ICON_EMOJI[notification.typeIcon] || "\u{1F514}";
        const title = `${emoji} ${notification.title}`;

        try {
            const nativeNotif = new Notification(title, {
                body: notification.message,
                /* BYŁO ŹLE: zaszyte "/favicon.ico", czyli statyczny plik z folderu aplikacji.
                   SKUTEK: powiadomienia systemowe pokazywały domyślną ikonę nawet wtedy, gdy
                   administrator wgrał własną w panelu Branding — dokładnie to zgłoszenie.
                   Endpoint zasobu sam przekierowuje na plik domyślny, gdy nic nie wgrano,
                   więc odwrót jest po stronie serwera i nie trzeba go powtarzać tutaj. */
                icon: "/api/branding/asset/favicon",
                badge: "/api/branding/asset/favicon",
                tag: notification.id || `notif-${Date.now()}`,
                requireInteraction: false,
                silent: false,
            });

            // Kliknięcie w powiadomienie systemowe → focus na okno + nawiguj do linku
            nativeNotif.onclick = () => {
                window.focus();
                nativeNotif.close();
                if (!notification.link) return;
                // [A16-09] BYLO ZLE: adres z powiadomienia szedl prosto do window.location.href, bez zadnego
                // sprawdzenia, czym w ogole jest. translatePath oddaje nieznana sciezke bez zmian, wiec
                // wartosc typu "javascript:..." albo "//obcy.example" tez by przeszla.
                // SKUTEK DLA UZYTKOWNIKA: dzis wszystkie linki buduje backend z szablonow, wiec nikt tego nie
                // odczuwa - to zabezpieczenie na wypadek powiadomien z adresem wpisywanym recznie, gdzie
                // klikniecie dymka moglo wyrzucic czlowieka z aplikacji na obca strone.
                // NAPRAWA: przechodzimy tylko na sciezke WEWNETRZNA (jeden ukosnik na poczatku, wiec bez
                // "//host"), a adres zewnetrzny wylacznie po https i w nowej karcie bez dostepu do okna
                // aplikacji. Wszystko inne jest ignorowane - klikniecie po prostu ustawia okno na wierzchu.
                const target = translatePath(notification.link);
                if (typeof target !== "string") return;
                if (/^\/(?!\/)/.test(target)) {
                    window.location.href = target;
                } else if (/^https:\/\//i.test(target)) {
                    window.open(target, "_blank", "noopener,noreferrer");
                }
            };

            // Auto-zamknij po 8 sekundach
            setTimeout(() => nativeNotif.close(), 8000);
        } catch (err) {
            // silently handled
        }
    }, []); // puste deps — używamy refów

    /* 🔴 [H1 2026-09-19] TOZSAMOSC KONTA, A NIE OBIEKT KONTA, W ZALEZNOSCIACH.
       ZMIERZONE (zapadka `__tests__/sufitZadanLandingow.h1`): wejscie na landing wysylalo
       `GET /api/notifications/channel` DWA RAZY. `AuthContext.checkLoggedIn` wola `setUser` dwa
       razy na kazde wejscie (optymistycznie z localStorage, potem po `GET /users/me`) — konto to
       samo, OBIEKT nowy. Ta sama naprawa, co w `components/Layout.js` (`idKonta`). */
    const idKontaKanalu = tozsamoscKonta(user);

    // Synchronizacja flagi desktop z backendem (kanał per USER, nie per przeglądarka).
    useEffect(() => {
        if (!idKontaKanalu) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiService.getNotificationChannel();
                if (cancelled || !res?.data) return;
                const enabled = res.data.windowsEnabled !== false;
                setDesktopEnabled(enabled);
                localStorage.setItem("desktopNotifications", enabled ? "true" : "false");
            } catch { /* offline — zostaje cache z localStorage */ }
        })();
        return () => { cancelled = true; };
    }, [idKontaKanalu]);

    // Automatycznie poproś o pozwolenie przy logowaniu
    useEffect(() => {
        if (user && desktopEnabled && typeof Notification !== "undefined" && Notification.permission === "default") {
            // Opóźnienie 3s żeby nie atakować użytkownika od razu
            const timer = setTimeout(() => {
                requestDesktopPermission();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [user, desktopEnabled, requestDesktopPermission]);

    // Fetch notifications z API
    const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
        try {
            const res = await apiService.getNotifications(pageNum, 20);
            const data = res.data || [];
            if (append) {
                setNotifications(prev => [...prev, ...data]);
            } else {
                setNotifications(data);
            }
            setHasMore(data.length === 20);
            setPage(pageNum);
        } catch (err) {
            // silently handled
        }
    }, []);

    // Load more (infinite scroll)
    const fetchMore = useCallback(() => {
        if (hasMore) {
            fetchNotifications(page + 1, true);
        }
    }, [hasMore, page, fetchNotifications]);

    /** Scalenie strony z listą: bez duplikatów po id, od najnowszego. Pozycja już obecna zachowuje WERSJĘ Z LISTY
        (mogła zostać odczytana lokalnie chwilę temu — odpowiedź serwera bywa starsza niż to kliknięcie). */
    const scalPowiadomienia = (prev, przychodzace) => {
        const znane = new Set(prev.map((n) => String(n.id)));
        const nowe = przychodzace.filter((n) => n && n.id != null && !znane.has(String(n.id)));
        if (nowe.length === 0) return prev;
        return [...prev, ...nowe].sort((a, b) => {
            const ta = new Date(a.createdAt).getTime() || 0;
            const tb = new Date(b.createdAt).getTime() || 0;
            return tb - ta;
        });
    };

    // Strona NIEPRZECZYTANYCH (bez zawężenia po dacie) — licznik ustawiany z TEJ SAMEJ odpowiedzi.
    const fetchUnread = useCallback(async (pageNum = 1) => {
        try {
            const res = await apiService.getUnreadNotifications(pageNum, 20);
            const items = res?.data?.items || [];
            const total = res?.data?.unreadTotal;
            setNotifications((prev) => scalPowiadomienia(prev, items));
            if (typeof total === "number") setUnreadCount(total);
            setUnreadHasMore(items.length === 20);
            setUnreadPage(pageNum);
        } catch (err) {
            // silently handled
        }
    }, []);

    const fetchMoreUnread = useCallback(() => {
        if (unreadHasMore) fetchUnread(unreadPage + 1);
    }, [unreadHasMore, unreadPage, fetchUnread]);

    // Mark as read
    const markAsRead = useCallback(async (id) => {
        // Powiadomienia syntetyczne (lokalne, np. HR) mają id z prefiksem "hr_" — nie wołamy API z nieistniejącym ID.
        const isLocal = typeof id === "string" && id.startsWith("hr_");
        const wasUnread = notifications.some(n => n.id === id && !n.isRead);

        setNotifications(prev =>
            prev.map(n => n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)
        );
        // Aktualizujemy licznik lokalnie (badge dzwonka) — nie czekamy wyłącznie na push z SignalR.
        if (wasUnread) setUnreadCount(c => Math.max(0, c - 1));

        if (isLocal) return;
        try {
            await apiService.markNotificationAsRead(id);
        } catch (err) {
            // silently handled
        }
    }, [notifications]);

    /* [2026-09-30, polecenie właściciela B3] „jak coś kliknę, a było powiadomieniem, to rób jako odczytany i tylko realnie
       nieodczytane zostaw”. Otwarcie OBIEKTU (karta zgłoszenia) oznacza wszystkie powiadomienia o nim — serwer robi to
       po kluczu obiektu (`PUT notifications/read-for-object`) i rozgłasza `NotificationsReadMany` do wszystkich kart
       i urządzeń konta. Tu tylko optymistycznie gasimy pozycje, których link wskazuje ten obiekt (ten sam wzór co serwer).
       ⚠️ Licznik NIE jest zmniejszany lokalnie: nie wiemy, ile pozycji spoza wczytanej strony serwer oznaczył, a
       `ReceiveUnreadCount` przychodzi z serwera autorytatywnie — dwa źródła prawdy o tej samej liczbie by się rozjechały. */
    const markReadForObject = useCallback(async (rodzaj, id) => {
        if (!rodzaj || id == null || id === "") return;
        /* Bez metody API (brak wpisu powiadomieniaObiektu w api/modules/index.js) NIE gasimy niczego lokalnie: dzwonek
           pokazywałby licznik z serwera obok listy bez nieprzeczytanych, a po odświeżeniu pozycje wróciłyby — dokładnie
           błąd, który zgłasza właściciel. Głośno w trybie deweloperskim, zamiast cichego pominięcia. */
        if (typeof apiService.markNotificationsReadForObject !== "function") {
            if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.warn("[B3] apiService.markNotificationsReadForObject nie istnieje — brak modułu powiadomieniaObiektu w api/modules/index.js; odczyt po obiekcie nie dotrze do serwera.");
            }
            return;
        }
        setNotifications(prev => {
            let zmiana = false;
            const nowa = prev.map(n => {
                // Sprawa „Do decyzji” zostaje nieprzeczytana — otwarcie obiektu nie jest decyzją (lustro serwera).
                if (n.isRead || czekaNaDecyzjeWOdczycie(n) || !linkWskazujeObiekt(n.link, rodzaj, id)) return n;
                zmiana = true;
                return { ...n, isRead: true, readAt: new Date().toISOString() };
            });
            return zmiana ? nowa : prev;
        });
        try {
            await apiService.markNotificationsReadForObject(rodzaj, id);
        } catch (err) {
            // Wygoda, nie dane: nieudany odczyt zostawia powiadomienia do kliknięcia — niczego nie psuje.
        }
    }, []);

    // Mark all as read
    const markAllAsRead = useCallback(async () => {
        try {
            await apiService.markAllNotificationsAsRead();
            setNotifications(prev =>
                prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
            );
            setUnreadCount(0);
        } catch (err) {
            // silently handled
        }
    }, []);

    // Delete notification
    const deleteNotification = useCallback(async (id) => {
        const isLocal = typeof id === "string" && id.startsWith("hr_");
        const wasUnread = notifications.some(n => n.id === id && !n.isRead);

        setNotifications(prev => prev.filter(n => n.id !== id));
        if (wasUnread) setUnreadCount(c => Math.max(0, c - 1));

        if (isLocal) return;
        try {
            await apiService.deleteNotification(id);
        } catch (err) {
            // silently handled
        }
    }, [notifications]);

    // Toggle panel
    const togglePanel = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const closePanel = useCallback(() => {
        setIsOpen(false);
    }, []);

    // ── Initial data fetch (independent of SignalR) ──
    // Ensures bell widget & sidebar badges work even when SignalR fails
    const initialFetchDone = useRef(false);

    useEffect(() => {
        // [HASLA-LOKALNE 2026-09-27] Hasło do zmiany = sesja służy wyłącznie zmianie hasła (serwer odrzuca resztę).
        if (!user || user.hasloDoZmiany === true) {
            initialFetchDone.current = false;
            return;
        }
        if (initialFetchDone.current) return;
        initialFetchDone.current = true;

        const loadInitialData = async () => {
            try {
                const [notifRes, countRes] = await Promise.allSettled([
                    apiService.getNotifications(1, 20),
                    apiService.getUnreadCount(),
                ]);

                if (notifRes.status === "fulfilled") {
                    const data = notifRes.value.data || [];
                    setNotifications(data);
                    setHasMore(data.length === 20);
                    setPage(1);

                    // Initialize sidebar badges from unread notifications
                    // (delayed slightly to ensure Sidebar has registered its callback)
                    setTimeout(() => {
                        data.forEach(n => {
                            if (!n.isRead) {
                                powiadomOKropce(n);
                            }
                        });
                    }, 100);
                }

                if (countRes.status === "fulfilled") {
                    const count = countRes.value.data?.count ?? 0;
                    setUnreadCount(count);
                }
            } catch (err) {
                // silently handled
            }
        };

        loadInitialData();
    }, [user]);

    // SignalR Connection (real-time updates)
    useEffect(() => {
        // [HASLA-LOKALNE 2026-09-27] Przy wymuszonej zmianie hasła hub też odpowiada 403 — bez prób łączenia.
        if (!user || user.hasloDoZmiany === true) {
            // Disconnect if logged out
            if (connectionRef.current) {
                connectionRef.current.stop();
                connectionRef.current = null;
            }
            // BYŁO ŹLE: zerowanie flagi siedziało WEWNĄTRZ `if (connectionRef.current)`, więc gdy hub
            // był już zatrzymany inną drogą (onclose, wygaszenie), po wylogowaniu `isConnected`
            // zostawało `true` — stan mówił „połączony" o połączeniu, którego nie ma.
            // Dziś nikt tej flagi nie konsumuje, więc objawu nie widać; to pułapka na pierwszego,
            // kto jej użyje (np. wskaźnik „na żywo" w interfejsie pokazałby zieloną kropkę po wylogowaniu).
            // NAPRAWA: flaga jest zerowana bezwarunkowo, bo po wylogowaniu połączenia NIE MA — niezależnie
            // od tego, czy akurat my je zamykaliśmy.
            setIsConnected(false);
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        const token = getSignalRToken();
        if (!token) return;

        const connection = new HubConnectionBuilder()
            .withUrl(HUB_URL, {
                // [KA-12 2026-09-26] Serwer zamyka połączenie huba z wygaśnięciem tokenu (CloseOnAuthenticationExpiration).
                // Żeby uczciwa karta nie traciła powiadomień na czas wyczerpywania prób ponownego połączenia, token
                // wygasły albo wygasający w ciągu minuty odświeżamy PRZED połączeniem (/users/me; przy wygasłym
                // ciasteczku przechwytywacz 401 zrobi po drodze /users/refresh).
                accessTokenFactory: () => tokenHubaSwiezy(),
                transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
            })
            // [P5-06] Te same odstępy, ale nieudana próba wymusza rotację sesji dla huba (token z nieważnym stemplem).
            .withAutomaticReconnect(politykaPonownegoPolaczenia)
            .configureLogging({
                log: (logLevel, message) => {
                    // Wycisz łagodne transienty negocjacji (React StrictMode/dev double-mount, restart backendu) —
                    // są nieszkodliwe i obsługiwane przez auto-reconnect; nie zaśmiecają konsoli przy odświeżaniu.
                    if (typeof message === "string" && message.toLowerCase().includes("negotiation")) {
                        return;
                    }
                    if (logLevel >= LogLevel.Warning) {
                        // eslint-disable-next-line no-console
                        console.warn(`[SignalR] ${message}`);
                    }
                },
            })
            .build();

        // Event: kanał WINDOWS bez kanału aplikacji — wyłącznie natywny toast systemowy,
        // zero śladu w centrum powiadomień (backend celowo nie utworzył wiersza).
        connection.on("ReceiveDesktopNotification", (payload) => {
            showDesktopNotification(payload);
        });

        // Event: nowe powiadomienie
        connection.on("ReceiveNotification", (notification) => {
            setNotifications(prev => [notification, ...prev]);
            /* [OBSZAR 20, 2026-09-18] BYŁO: centrum powiadomień nasłuchiwało `notification-received`, którego NIKT nie
               wysyłał (komentarz w NotificationCenterPage obiecywał to od S12). Nowa pozycja lądowała w dzwonku,
               a w otwartym centrum dopiero po F5. */
            window.dispatchEvent(new CustomEvent(ZDARZENIA_SYNC.received, { detail: notification }));
            // Toast callback (w aplikacji)
            if (toastCallbackRef.current) {
                toastCallbackRef.current(notification);
            }
            // Sidebar badge callback (zielona kropka)
            {
                powiadomOKropce(notification);
            }
            // Natywne powiadomienie systemowe Windows
            showDesktopNotification(notification);
            // [AUDYT 2026-07-27] Live-refresh PORTALU ZGŁASZAJĄCEGO: odpowiedź obsługi albo zamknięcie
            // ma odświeżyć „Moje zgłoszenia" bez F5 (W-3 żywego modułu). Kontrakt jak w HR:
            // SignalR → zdarzenie window → strona sama się przeładowuje.
            if (typeof notification?.typeCode === "string"
                && notification.typeCode.endsWith("_for_reporter")) {
                window.dispatchEvent(new CustomEvent("my-ticket-changed", { detail: notification }));
            }
            /* [zgłoszenie właściciela 2026-09-11] ETAPY REALIZACJI — live-refresh panelu „Zaawansowane".
               Panel etapów nasłuchuje zdarzenia `kroki-zgloszenia-zmiana`; bez tego wiersza lista
               odświeżała się WYŁĄCZNIE po własnej akcji, więc druga osoba prowadząca tę samą sprawę
               nie widziała zmiany do czasu ponownego otwarcia karty. Kanał jest kanoniczny
               (CLAUDE.md): serwer → NotificationHub → to miejsce → zdarzenie okna → panel sam się
               odświeża. Powiadomienie o etapie leci też jako `_for_reporter`, więc portal pracownika
               ożywa wyżej tą samą drogą. */
            if (typeof notification?.typeCode === "string" && notification.typeCode.startsWith("ticket_krok")) {
                window.dispatchEvent(new CustomEvent("kroki-zgloszenia-zmiana", { detail: notification }));
            }
            // [DYSKI 2026-07-27] Live-refresh dostępów do dysków: wniosek/decyzja/utrata właściciela
            // ożywia stronę przełożonego (/moj-dzial/uprawnienia) i admina (/admin/drives) bez F5.
            if (typeof notification?.typeCode === "string" && notification.typeCode.startsWith("drive_")) {
                window.dispatchEvent(new CustomEvent("drive-access-changed", { detail: notification }));
            }
            // Live-refresh modułu Wnioski HR (wnioskodawca + akceptant) — W-3 żywy moduł.
            if (typeof notification?.typeCode === "string" && notification.typeCode.startsWith("hr_")) {
                window.dispatchEvent(new CustomEvent("hr-request-changed", { detail: notification }));
            }
            // [A09-21] BYŁO ŹLE: alias okna 'hr-notification' — jedyny kanał, którym SignalR ożywia podstrony
            // „Mój Dział" — leciał WYŁĄCZNIE dla typów zaczynających się od "hr_". Powiadomienia pracy zdalnej
            // (remote_report_window), grafiku (schedule_approved/changed/fill_reminder) i urlopów
            // (vacation_pending/approved/rejected) nigdy go nie wywoływały.
            // SKUTEK DLA UŻYTKOWNIKA: pracownik składał raport pracy zdalnej albo wniosek urlopowy, a strona
            // przełożonego (/moj-dzial/praca-zdalna, /moj-dzial/plan-urlopow, pulpit działu) stała nieruszona —
            // przełożony dowiadywał się, że coś na niego czeka, dopiero po ręcznym odświeżeniu (F5).
            // NAPRAWA: jedna lista prefiksów zamiast zaszytego "hr_". Podwójne powiadomienie nie grozi:
            // lokalny nasłuch tego samego aliasu odcina zdarzenia z SignalR po znaczniku detail.notification.
            if (typeof notification?.typeCode === "string"
                && PREFIKSY_ALIASU_HR.some((p) => notification.typeCode.startsWith(p))) {
                // Alias dla podstron /moj-dzial nasłuchujących 'hr-notification' (detail.type) — jeden kontrakt,
                // dzięki czemu SignalR (akcja innego usera) ożywia pulpit/wnioski/urlopy/zdalną przełożonego.
                window.dispatchEvent(new CustomEvent("hr-notification", { detail: { type: notification.typeCode, notification } }));
            }
            // [AKCEPTACJE 2026-08-29] „Coś czeka na Twoją decyzję" ma się przeliczyć od razu — licznik
            // na pulpicie i kropka w menu nie mogą czekać do następnego wejścia na stronę.
            // Rozpoznajemy po KATEGORII z bazy (a nie po liście kodów), żeby typ dosiany w DbInitializerze
            // ożywiał licznik bez zmiany we froncie; lista kodów jest tylko zabezpieczeniem na wypadek,
            // gdyby ujednolicenie kategorii jeszcze się nie przetoczyło na tym środowisku.
            if (notification?.typeCategory === KATEGORIA_AKCEPTACJE
                || KODY_AKCEPTACJI.includes(notification?.typeCode)) {
                window.dispatchEvent(new CustomEvent("approvals-changed", { detail: notification }));
            }
        });

        // Event: zaktualizowana liczba nieprzeczytanych
        connection.on("ReceiveUnreadCount", (count) => {
            setUnreadCount(count);
        });

        /* 🔴 [zgłoszenie właściciela 2026-09-17] „jak odznaczam coś, co przeczytałem na laptopie, to często
           jest tak, że na PC dalej mam ten sam komunikat (…) mam wrażenie, że ta synchronizacja SignalR
           nie działa poprawnie."

           CO BYŁO ŹLE: serwer rozgłaszał WYŁĄCZNIE `ReceiveUnreadCount`, a ten obsługiwaliśmy jednym
           `setUnreadCount`. Drugie urządzenie dostawało więc poprawnie zmniejszony licznik przy dzwonku,
           ale jego TABLICA powiadomień zostawała nietknięta — pozycja dalej wyglądała na nieprzeczytaną.
           SignalR działał; wysyłał za mało, a front stosował to za wąsko.

           TERAZ serwer dokłada zdarzenia Z IDENTYFIKATOREM, a my stosujemy je do listy. Licznik zostaje
           wartością autorytatywną z bazy — te zdarzenia go nie ruszają, żeby dwa źródła prawdy o tej samej
           liczbie nie zaczęły się rozjeżdżać.

           ⚠️ Porównanie identyfikatorów przez `String(...)`: z serwera przychodzi Guid, a lista trzyma też
           pozycje syntetyczne z identyfikatorem tekstowym (prefiks „hr_"). Porównanie `===` na surowych
           wartościach cicho by ich nie trafiło. */
        /* [OBSZAR 20, 2026-09-18] Reguła zastosowania każdego z tych zdarzeń mieszka w JEDNEJ funkcji
           (`zastosujSync`), a samo zdarzenie jest re-emitowane do okna — centrum powiadomień trzyma własną
           listę i musi dostać to samo, co dzwonek. Dwie kopie tej reguły rozjechałyby się przy pierwszej zmianie. */
        const przekazSync = (nazwa, detail) => {
            setNotifications(prev => zastosujSync(prev, nazwa, detail));
            window.dispatchEvent(new CustomEvent(nazwa, { detail }));
        };

        connection.on("NotificationRead", (id) => przekazSync(ZDARZENIA_SYNC.read, id));
        // [2026-09-30, B3] Odczyt po OBIEKCIE (otwarta karta / rodzeństwo klikniętego powiadomienia) — lista identyfikatorów.
        connection.on("NotificationsReadMany", (ids) => {
            if (!Array.isArray(ids) || ids.length === 0) return;
            przekazSync(ZDARZENIA_SYNC.readMany, ids);
        });
        connection.on("NotificationsAllRead", () => przekazSync(ZDARZENIA_SYNC.allRead, null));
        connection.on("NotificationRemoved", (id) => przekazSync(ZDARZENIA_SYNC.removed, id));
        connection.on("NotificationRemovedByLink", (link) => {
            if (!link) return;
            przekazSync(ZDARZENIA_SYNC.removedByLink, link);
        });
        /* Retencja w tle skasowała stare pozycje — BYŁO: bez żadnego sygnału, więc karta otwarta od tygodni
           pokazywała pozycje, których w bazie już nie ma, a licznik stał na wartości sprzed sprzątania. */
        connection.on("NotificationsPruned", (progi) => przekazSync(ZDARZENIA_SYNC.pruned, progi));

        // Event: nowy komunikat wymuszający wyświetlenie modala
        connection.on("ReceiveAnnouncementAlert", (announcementId) => {
            if (announcementCallbackRef.current) {
                announcementCallbackRef.current(announcementId);
            }
        });

        // Event: aktualizacja regulaminu — wymusza ponowne sprawdzenie
        connection.on("ReceiveTermsUpdate", () => {
            if (termsCallbackRef.current) {
                termsCallbackRef.current();
            }
        });

        /* 🔴 [zgłoszenie właściciela 2026-09-17] Dwa pozostałe tory tej samej skargi. Powiadomienia
           z dzwonka synchronizują się wyżej; komunikaty i regulamin nie synchronizowały się WCALE —
           serwer nie wysyłał o nich nic, więc drugie, otwarte urządzenie stało z modalem bramki
           do przeładowania strony. Teraz serwer mówi, a my to stosujemy. */
        connection.on("AnnouncementRead", (announcementId) => {
            if (announcementReadCallbackRef.current) {
                announcementReadCallbackRef.current(announcementId);
            }
        });

        /* [polecenie właściciela 2026-10-04] Pasek komunikatu nad nagłówkiem ma się pojawić i zniknąć bez F5.
           Serwer wysyła sam sygnał (bez treści) po dodaniu, edycji, zakończeniu i usunięciu paska oraz gdy
           nadejdzie jego czas „od”. Pasek słucha zdarzenia okna i pyta serwer, co jest dla tego konta. */
        connection.on("PasekKomunikatuZmiana", () => {
            window.dispatchEvent(new Event("pasek-komunikatu-zmiana"));
        });

        connection.on("TermsAccepted", (termsId) => {
            if (termsAcceptedCallbackRef.current) {
                termsAcceptedCallbackRef.current(termsId);
            }
        });

        /* [A09-08] BYŁO ŹLE: stały tu dwa nasłuchy huba — "ReceiveHrRequestUpdate" i "ReceiveHrPendingRequest" —
           które budowały własne, syntetyczne powiadomienia HR i wpychały je do dzwonka, dymka i toastu
           systemowego. Backend NIGDY nie wysyłał żadnego z tych zdarzeń (zero emitentów w całym kodzie
           serwera, potwierdzone także w audycie z 2026-07-02), a linki, które te powiadomienia podstawiały
           (/moje-wnioski-hr, /wnioski-dzialu, /hr-zespol-oczekujace), nie istnieją w mapie tras — kliknięcie
           przerzuciłoby człowieka na pulpit.
           SKUTEK DLA UŻYTKOWNIKA: żaden bezpośredni (kod nigdy się nie wykonywał). Koszt był w utrzymaniu:
           dokumentacja i kolejne osoby zakładały, że powiadomienia HR w czasie rzeczywistym idą tą ścieżką,
           więc realnego braku szukano nie tam, gdzie był.
           NAPRAWA: martwe nasłuchy usunięte. Realne powiadomienia HR przychodzą — i przychodziły przez cały
           czas — jako zwykłe "ReceiveNotification" z typami hr_*, obsługiwane wyżej razem z aliasem
           'hr-notification'. Nic, co dziś działa dla człowieka, nie zostało ruszone. */

        // Event: aktualności (blog) — pojawienie się / aktualizacja / usunięcie BEZ odświeżania.
        // Rozsyłamy jako globalne zdarzenia okna; nasłuchują strony /aktualnosci i /aktualnosci/:slug.
        connection.on("NewsUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("news-upserted", { detail: dto }));
        });
        connection.on("NewsRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("news-removed", { detail: id }));
        });

        // Zmiana struktury działu (przeniesienie/assign/remove) — odśwież Strukturę
        // (Mój Dział) i Książkę Adresową na żywo, także w innych sesjach.
        connection.on("DeptStructureChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("dept-structure-changed", { detail: payload }));
        });

        // [R9 2026-09-05] PRZEŁOŻONY I UPRAWNIENIA NA ŻYWO. Dotąd zmiana przełożonego w e-teczce i zmiana
        // uprawnień w panelu dostępu nie nadawały NIC — strony nasłuchiwały "access-control-changed",
        // którego żaden serwer nie wysyłał (dispatchował je tylko sam panel po własnym zapisie, więc druga
        // otwarta karta i kartoteka widziały stary stan aż do F5). Ładunek to sygnał „odśwież" (powód, czas,
        // liczba kont) — BEZ identyfikatorów osób, bo leci do każdej sesji w firmie (reguła A09-15).
        // Nasłuchują: panel dostępu (podsumowanie + zakładki), kartoteka, działy, e-teczka (karta
        // przynależności), schemat organizacyjny.
        connection.on("SupervisorChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("supervisor-changed", { detail: payload }));
        });
        connection.on("AccessControlChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("access-control-changed", { detail: payload }));
        });

        // Zmiana SZKICU struktury organizacyjnej (nowa pozycja planu, publikacja, wycofanie).
        // Serwer wysyła ten sygnał WYŁĄCZNIE do posiadaczy klucza planowania — planowana reorganizacja
        // to informacja kadrowa, więc nie leci do Clients.All jak sygnał o zmianie działu.
        connection.on("OrgStructureDraftChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("org-structure-draft-changed", { detail: payload }));
        });

        // Galeria — analogicznie (pojawienie/edycja/usunięcie bez odświeżania).
        connection.on("GalleryUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("gallery-upserted", { detail: dto }));
        });
        connection.on("GalleryRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("gallery-removed", { detail: id }));
        });

        // Konkursy — analogicznie (pojawienie/edycja/ukrycie/usunięcie bez odświeżania).
        connection.on("ContestUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("contest-upserted", { detail: dto }));
        });
        connection.on("ContestRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("contest-removed", { detail: id }));
        });

        // E-Learning — kursy pojawiają się/aktualizują/znikają bez odświeżania (wg widoczności per-user).
        connection.on("CourseUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("course-upserted", { detail: dto }));
        });
        connection.on("CourseRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("course-removed", { detail: id }));
        });
        // [polecenie właściciela 2026-09-14] Pakiety szkoleń — zapis/dezaktywacja/przypisanie pakietu (backend wysyła imiennie:
        // osoby przypisane + administratorzy). Zarządzanie, e-teczka i „Moje pakiety” odświeżają listę bez przeładowania.
        connection.on("PakietSzkolenUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("pakiet-szkolen-upserted", { detail: dto }));
        });

        // Baza wiedzy — artykuły (wg widoczności per-user) bez odświeżania.
        connection.on("KbArticleUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("kb-article-upserted", { detail: dto }));
        });
        connection.on("KbArticleRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("kb-article-removed", { detail: id }));
        });

        // FAQ — pytania/odpowiedzi (wg widoczności per-user) bez odświeżania.
        connection.on("FaqUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("faq-upserted", { detail: dto }));
        });
        connection.on("FaqRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("faq-removed", { detail: id }));
        });

        // [A09-22] Ankiety — pojawienie/edycja/ukrycie/usunięcie bez odświeżania (wg widoczności per-user).
        // Ładunkiem jest samo ID (nie DTO jak w Aktualnościach): pozycja listy ankiet niesie „czy już
        // wypełniłem", które jest INNE dla każdej osoby, więc strona dociąga swoją wersję listy sama.
        connection.on("SurveyUpserted", (id) => {
            window.dispatchEvent(new CustomEvent("survey-upserted", { detail: id }));
        });
        connection.on("SurveyRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("survey-removed", { detail: id }));
        });

        // Dokumenty firmowe — pojawienie/edycja/ukrycie/usunięcie (wg widoczności per-user).
        connection.on("DocumentUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("document-upserted", { detail: dto }));
        });
        connection.on("DocumentRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("document-removed", { detail: id }));
        });
        // [POLITYKI REDAKCJA 2026-09-29, W-1] Zdarzenie modułu polityk (PolitykiService.RozeslijAsync → PolitykaZdarzeniaHuba.PolitykaZmieniona):
        // /polityki odświeża rejestr i otwartą szufladę bez przeładowania (założenie, metadane, nowa wersja, zamrożenie, publikacja).
        connection.on("PolitykaUpserted", (payload) => {
            window.dispatchEvent(new CustomEvent("polityka-upserted", { detail: payload }));
        });
        // [POLITYKI POTWIERDZENIA 2026-09-29, W-1] Obowiązek/potwierdzenie/storno tej osoby (PolitykaZdarzeniaHuba.ObowiazekZmieniony):
        // /do-potwierdzenia, kafel pulpitu i panel potwierdzeń odświeżają się bez F5.
        // [E8 2026-09-30, INTEGRACJA, W-1] Moduł „Wykaz KSC”: serwer rozgłasza SYGNAŁ BEZ TREŚCI (dane wpisu są need-to-know, E8 §8)
        // do posiadaczy WykazKscPodglad; strony modułu (trasy /wykaz-ksc i podstrony) nasłuchują przez pages/wykazKsc/useWykazKscLive.js z odbiciem 400 ms.
        connection.on("WykazKscUpserted", () => {
            window.dispatchEvent(new CustomEvent("wykazksc-upserted"));
        });
        // [O365-F 2026-10-04, W-1] Moduł „Zarządzanie Office 365”: serwer (O365Rozglos) rozgłasza SYGNAŁ BEZ DANYCH { companyId, rodzaj }
        // do posiadaczy Office365View w zasięgu spółki; zakładki /admin/office365 słuchają zdarzenia okna „o365bezp-zmiana” (useO365Live,
        // odbicie 400 ms) i dociągają dane z API pilnującego klucza i zasięgu.
        connection.on("O365BezpZmiana", (payload) => {
            window.dispatchEvent(new CustomEvent("o365bezp-zmiana", { detail: payload }));
        });
        connection.on("PolitykaObowiazekUpserted", (payload) => {
            window.dispatchEvent(new CustomEvent("polityka-obowiazek-upserted", { detail: payload }));
        });

        /* [2026-09-30, MOSTY HUBA polecenia właściciela] Jedyne miejsce, które re-emituje zdarzenia huba do okna (W-3:
           SignalR → most → zdarzenie okna → refetch). Strony tylko SŁUCHAJĄ — nie dispatchują tych zdarzeń same.
           Kontrakt nazw (serwer → okno, detail = ładunek bez treści spraw; dane dociąga API pilnujące dostępu):
           - "TicketChanged" → "ticket-changed" ({ ticketId, ticketIds[], rodzaj }) — żywa kolejka i karta zgłoszenia u obsady
             („to musi być aktualizowane też u innych pracowników bez odświeżania strony”);
           - "SkrzynkaZewnetrznaZmiana" → "skrzynka-zewnetrzna-changed" ({ configurationId, oczekujace }) — licznik katalogu
             „Zewnętrzne” (poczta spoza domen spółek);
           - "ProceduryModuluZmiana" → "procedury-modulu-zmiana" ({ moduleId, kod }) — sekcja „Procedury” modułu obsługi;
           - "KbZapoznanieZmiana" → "kb-zapoznanie-zmiana" ({ articleId }) — obowiązek zapoznania się z artykułem bazy wiedzy;
           - [domknięcie C 2026-10-01] "TeamViewerEwidencjaZmiana" → "teamviewer-ewidencja-zmiana" ({ osobaId }) — przypisanie
             komputera TeamViewera (sekcja „Zdalne połączenie” karty zgłoszenia, /itam/teamviewer);
           - [P13 2026-10-01] "FunkcjeModuluZmiana" → "funkcje-modulu-zmiana" ({ moduleId, kod }) — przełączniki funkcji modułu
             (szuflada „Edytuj moduł” → „Raporty”) i funkcje administracyjne modułu: karta, okno zamknięcia i kolejka
             odświeżają flagi bez przeładowania strony. */
        connection.on("TicketChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("ticket-changed", { detail: payload }));
        });
        connection.on("SkrzynkaZewnetrznaZmiana", (payload) => {
            window.dispatchEvent(new CustomEvent("skrzynka-zewnetrzna-changed", { detail: payload }));
        });
        connection.on("ProceduryModuluZmiana", (payload) => {
            window.dispatchEvent(new CustomEvent("procedury-modulu-zmiana", { detail: payload }));
        });
        connection.on("KbZapoznanieZmiana", (payload) => {
            window.dispatchEvent(new CustomEvent("kb-zapoznanie-zmiana", { detail: payload }));
        });
        connection.on("TeamViewerEwidencjaZmiana", (payload) => {
            window.dispatchEvent(new CustomEvent("teamviewer-ewidencja-zmiana", { detail: payload }));
        });
        connection.on("FunkcjeModuluZmiana", (payload) => {
            window.dispatchEvent(new CustomEvent("funkcje-modulu-zmiana", { detail: payload }));
        });
        /* [polecenie właściciela 2026-10-02] "TrybTestowyPowiadomienZmiana" → "tryb-testowy-powiadomien-zmiana" ({ spolkaIds }):
           ktoś włączył albo wyłączył tryb testowy powiadomień spółki — baner nad nagłówkiem, komunikat na /admin i suwak
           przy firmie pytają serwer od nowa (`useTrybTestowy`). Ładunek to same identyfikatory spółek. */
        connection.on("TrybTestowyPowiadomienZmiana", (payload) => {
            window.dispatchEvent(new CustomEvent("tryb-testowy-powiadomien-zmiana", { detail: payload }));
        });
        /* [poprawka O365-I 2026-10-04] "O365RegulySpolekZmiana" → "o365-reguly-spolek-zmiana" ({ companyId, rodzaj: "reguly" }):
           ktoś przestawił monitorowanie, regułę albo datę poinformowania spółki w /admin/integrations → „Reguły incydentów”.
           Serwer wysyła to imiennie administratorom z dostępem do integracji (ci sami, którzy widzą sekcję); sekcja pobiera listę
           od nowa (`RegulyIncydentowSekcja`). Ładunek to sam identyfikator spółki. */
        connection.on("O365RegulySpolekZmiana", (payload) => {
            window.dispatchEvent(new CustomEvent("o365-reguly-spolek-zmiana", { detail: payload }));
        });

        // Zadania (/zadania) — utworzenie/edycja/przypisanie/ukończenie/usunięcie u zaangażowanych.
        // Live-update listy bez F5: gdy ktoś coś zmieni, strona /zadania refetchuje.
        connection.on("TaskUpserted", (id) => {
            window.dispatchEvent(new CustomEvent("task-upserted", { detail: id }));
        });
        connection.on("TaskRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("task-removed", { detail: id }));
        });

        // Kanban — zmiana na tablicy (karta/kolumna/członek) u WSZYSTKICH zaangażowanych.
        // Live-update bez F5: KanbanPage refetchuje otwartą tablicę + listę tablic.
        connection.on("KanbanBoardChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("kanban-board-changed", { detail: payload }));
        });

        // Listy projektowe — zmiana w projekcie ({projectId}) u WSZYSTKICH członków.
        // Live-update bez F5: /projekty i /projekty/:id refetchują po tym zdarzeniu.
        connection.on("PlProjectChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("pl-project-changed", { detail: payload }));
        });

        // Kalendarz — zmiana wykryta z Outlooka (reconcile/webhook). Live-refresh /kalendarz bez F5.
        connection.on("CalendarChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("calendar-changed", { detail: payload }));
        });

        // Konfigurator HR — obecność edycji (kto teraz edytuje typ) + informacja o zapisie innej sesji.
        connection.on("HrTypeEditingChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("hr-type-editing-changed", { detail: payload }));
        });
        connection.on("HrTypeSaved", (payload) => {
            window.dispatchEvent(new CustomEvent("hr-type-saved", { detail: payload }));
        });

        // [brief właściciela 2026-08-31, pkt 4.2] KOMENTARZ NA WNIOSKU HR — „jak ktoś daje komentarze
        // do wniosku to jest problem, nie mam komunikatu". Backend wysyła to zdarzenie WYŁĄCZNIE do
        // stron wniosku i jego akceptantów (Clients.Users, nigdy Clients.All), więc kropka zapala się
        // tylko tym, których sprawa dotyczy. Ładunek: { requestId, requestNumber, commentId,
        // createdAt, byUserId, byUserName }.
        connection.on("HrRequestCommentAdded", (payload) => {
            window.dispatchEvent(new CustomEvent("hr-comment-added", { detail: payload }));
        });

        /* ═══ [PAKIET 2026-09-02] Zdarzenia pięciu nowych modułów ═══════════════════════════════════
           Wszystkie nasłuchy powstają TUTAJ, w fundamencie, choć ich nadawcy (serwisy modułów)
           powstają dopiero w kolejnych falach. Powód jest ten sam co przy trasach i kafelkach:
           pięciu równoległych autorów dopisywałoby się do tego jednego bloku i wywracało sobie
           zmiany. Nasłuch bez nadawcy jest nieszkodliwy (nikt go nie woła); NADAWCA BEZ NASŁUCHU
           to cicha strata sygnału — moduł „nadawał", a w menu nic się nie działo.

           Kontrakt jest ten sam, co dla news/galerii: SignalR → zdarzenie `window` → strony i
           `useSidebarBadges` reagują. Backend wysyła te sygnały IMIENNIE (Clients.Users), nigdy
           Clients.All, więc kropka trafia wyłącznie do uprawnionych — bramka stoi po stronie
           nadawcy, nie tutaj. */

        // Ścieżka kariery (G) — nowe osiągnięcie w aktach, ocena kompetencji, wpis na osi.
        connection.on("CareerPathChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("career-changed", { detail: payload }));
        });

        // Cele (H) — nowy/zmieniony cel oraz usunięcie. Ładunek: id celu (postęp i „czyj to cel"
        // liczy się per osoba, więc strona dociąga swoją wersję listy sama — jak przy ankietach).
        connection.on("GoalUpserted", (id) => {
            window.dispatchEvent(new CustomEvent("goal-upserted", { detail: id }));
        });
        connection.on("GoalRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("goal-removed", { detail: id }));
        });

        // Oferty pracy (J) — publikacja/edycja/zdjęcie ogłoszenia wg zasięgu widoczności.
        connection.on("JobOfferUpserted", (dto) => {
            window.dispatchEvent(new CustomEvent("job-offer-upserted", { detail: dto }));
        });
        connection.on("JobOfferRemoved", (id) => {
            window.dispatchEvent(new CustomEvent("job-offer-removed", { detail: id }));
        });

        /* Rejestr wydań dokumentów (K + I-a) — JEDNO zdarzenie dla obu modułów, bo jest JEDEN rejestr
           (`DocumentIssues`, fundament §1.1). Projekt I-a przewidywał osobne „HrDocumentIssueUpserted";
           dwa sygnały o tym samym zapisie znaczyłyby dwie kropki i dwa refetche tej samej listy. */
        connection.on("DocumentIssueUpserted", (payload) => {
            window.dispatchEvent(new CustomEvent("document-issue-upserted", { detail: payload }));
        });

        // Onboarding (I-b) i offboarding (I-c) — zmiana w sprawie (pozycja odhaczona, konto założone,
        // sprawa zamknięta). Ładunek niesie id sprawy, żeby otwarta karta mogła się odświeżyć.
        connection.on("OnboardingUpserted", (payload) => {
            window.dispatchEvent(new CustomEvent("onboarding-upserted", { detail: payload }));
        });
        connection.on("OffboardingChanged", (payload) => {
            window.dispatchEvent(new CustomEvent("offboarding-changed", { detail: payload }));
        });

        connection.onreconnecting(() => {
            setIsConnected(false);
            // Zerwany socket to zwykle NAJSZYBSZY objaw padniętego serwera — szybciej niż kolejne
            // wywołanie API. Szturchamy globalny down-detektor; on i tak zweryfikuje /health,
            // więc chwilowy blip sieci (health wróci OK) nie pokaże ekranu przerwy.
            window.dispatchEvent(new Event("backend-network-error"));
        });

        connection.onreconnected(() => {
            setIsConnected(true);
            restartAttemptRef.current = 0;
            fetchNotifications(1, false);
            // [A09-07] Strony z podglądem na żywo (Kanban, projekty, kalendarz) przespały
            // okres bez połączenia — dajemy im jeden sygnał, żeby mogły dociągnąć zmiany.
            window.dispatchEvent(new Event("realtime-reconnected"));
        });

        connection.onclose(() => {
            setIsConnected(false);
            // Hub zamknięty na amen (wyczerpane próby withAutomaticReconnect) — potraktuj jak
            // awarię backendu i pozwól detektorowi zdecydować po sprawdzeniu /health.
            window.dispatchEvent(new Event("backend-network-error"));
            // [A09-07] BYŁO: na tym się kończyło — hub zostawał martwy do ręcznego F5.
            scheduleRestart();
        });

        // Listen for local HR notification events (localStorage fallback)
        const handleHrNotification = (e) => {
            const data = e.detail;
            if (!data) return;

            // [A15-01] Zdarzenie pochodzące z SignalR jest już OBSŁUŻONE — nie dubluj go.
            //
            // Co było źle: handler `ReceiveNotification` dla typów hr_* dokładał wpis do dzwonka,
            // pokazywał dymek i toast systemowy, a NA KOŃCU emitował alias okna „hr-notification"
            // (dla podstron /moj-dzial). Ten sam alias łapał poniższy handler — ten sam kontekst,
            // dispatch synchroniczny — i przerabiał go po raz drugi: typy backendu
            // (hr_request_status, hr_pending_approval, hr_low_leave_balance, hr_overdue_leave)
            // nie pasują do żadnego `case`, więc wpadały w `default`.
            // Skutek dla użytkownika: po decyzji przełożonego pracownik widział w dzwonku DWA wpisy
            // (drugi bezużyteczny „Powiadomienie HR / Aktualizacja w systemie HR" z martwym linkiem),
            // dwa dymki, dwa natywne toasty Windows, a licznik nieprzeczytanych był o 1 za wysoki.
            // Dlaczego tak naprawione: alias musi ZOSTAĆ (to jedyny kanał ożywiający strony
            // /moj-dzial — W-3 żywego modułu), więc odcinamy tylko drugą konsumpcję. Znacznikiem
            // pochodzenia jest `detail.notification` — dokłada je wyłącznie emiter z SignalR;
            // lokalne źródła (eTeczkaNotificationService, hrDataService._dispatchNotification)
            // wysyłają `detail` bez tego pola i działają dalej bez zmian.
            if (data.notification) return;

            let title, message, typeIcon, link;

            /* [A15-07] BYŁO ZLE: linki tych powiadomień były wpisane wprost jako „/moje-wnioski-hr",
               „/wnioski-dzialu" i „/hr-wnioski" — ŻADNEGO z tych adresów nie ma w mapie tras.
               Nieznana ścieżka wpada na trasę łapiącą wszystko (LocalizedRedirect), a ta kończy
               przeniesieniem na pulpit.
               SKUTEK DLA UŻYTKOWNIKA: kadrowa albo pracownik dostawał w dzwonku powiadomienie
               („Nowy dokument w e-Teczce", „Rozpoczęto offboarding", „Wniosek wymaga korekty"),
               klikał w nie i zamiast dokumentu czy wniosku lądował na pulpicie — bez słowa
               wyjaśnienia, że link nie prowadzi tam, gdzie obiecuje.
               NAPRAWA: adresy liczone z mapy tras przez localizedPath, więc są zawsze zgodne
               z tym, co naprawdę jest zarejestrowane, i w języku, w którym człowiek pracuje.
               Powiadomienia e-teczki prowadzą do e-teczki, a nie do wniosków kadrowych. */
            switch (data.type) {
                case "REQUEST_STATUS_CHANGED":
                    title = `Wniosek ${data.data?.requestNumber || ""}`;
                    message = `Status zmieniony przez ${data.data?.changedBy || "system"}`;
                    typeIcon = "FiFileText";
                    link = localizedPath("hrRequests");
                    break;
                case "NEW_REQUEST_PENDING":
                    title = "Nowy wniosek do akceptacji";
                    message = `${data.data?.userName || "Pracownik"} złożył(a) wniosek ${data.data?.requestNumber || ""}`;
                    typeIcon = "FiBriefcase";
                    link = localizedPath("myDeptRequests");
                    break;
                case "CORRECTION_REQUESTED":
                    title = "Wniosek wymaga korekty";
                    message = `HR zwrócił(a) wniosek ${data.data?.requestNumber || ""} do korekty: ${data.data?.reason || ""}`;
                    typeIcon = "FiEdit";
                    link = localizedPath("hrRequests");
                    break;
                case "REQUEST_RESUBMITTED":
                    title = "Wniosek ponownie złożony";
                    message = `${data.data?.userName || "Pracownik"} ponownie złożył(a) wniosek ${data.data?.requestNumber || ""} po korekcie`;
                    typeIcon = "FiRefreshCw";
                    // Kadrowa wraca do pełnej listy obsługi HR, przełożony do wniosków swojego działu.
                    link = data.data?.isHr ? localizedPath("hrFullView") : localizedPath("myDeptRequests");
                    break;
                default:
                    title = "Powiadomienie HR";
                    message = "Aktualizacja w systemie HR";
                    typeIcon = "FiFileText";
                    // Zdarzenia e-teczki (ETECZKA_*) mają swój moduł — kierujemy je tam, gdzie
                    // człowiek faktycznie zobaczy dokument, o którym mówi powiadomienie.
                    link = typeof data.type === "string" && data.type.startsWith("ETECZKA_")
                        ? localizedPath("eTeczka")
                        : localizedPath("hrRequests");
            }

            const hrNotification = {
                id: data.id || `hr_local_${Date.now()}`,
                title,
                message,
                typeIcon,
                link,
                isRead: false,
                createdAt: new Date().toISOString(),
            };
            setNotifications(prev => [hrNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
            if (toastCallbackRef.current) {
                toastCallbackRef.current(hrNotification);
            }
            showDesktopNotification(hrNotification);
        };

        window.addEventListener("hr-notification", handleHrNotification);

        /* ── [A09-07] Odzyskiwanie połączenia real-time ──
           BYŁO: withAutomaticReconnect próbuje 5 razy (~47 s); po ich wyczerpaniu albo gdy
           padł już PIERWSZY start(), zostawało tylko setIsConnected(false) — hub był martwy
           aż do ręcznego odświeżenia strony, a wskaźnik połączenia nie jest nigdzie
           pokazywany, więc użytkownik nawet o tym nie wiedział.
           SKUTEK DLA UŻYTKOWNIKA: po wygaśnięciu JWT (karta otwarta >120 min), po zmianie
           uprawnień (unieważnienie tokenu) albo po dłuższej przerwie sieci przestawały
           przychodzić powiadomienia, zielone kropki i podgląd na żywo — "nic mi nie przyszło".
           NAPRAWA: własny restart z rosnącym odstępem (5 s → 15 → 30 → 60 → 120 s, dalej co
           120 s) — bez agresywnej pętli bijącej w serwer. Przed każdą próbą jedno /users/me,
           które odświeża sesję i token SignalR (negocjacja ze starym tokenem kończyła się 401,
           więc bez tego restart i tak by nie pomógł). Gdy karta wraca na wierzch lub wraca
           sieć — próbujemy od razu, zamiast czekać do końca odstępu. */
        let disposed = false;

        const clearRestartTimer = () => {
            if (restartTimerRef.current) {
                clearTimeout(restartTimerRef.current);
                restartTimerRef.current = null;
            }
        };

        const scheduleRestart = () => {
            if (disposed || restartTimerRef.current) return;
            const idx = Math.min(restartAttemptRef.current, RECONNECT_DELAYS_MS.length - 1);
            restartAttemptRef.current += 1;
            restartTimerRef.current = setTimeout(() => {
                restartTimerRef.current = null;
                startConnection(true);
            }, RECONNECT_DELAYS_MS[idx]);
        };

        const startConnection = async (isRestart = false) => {
            if (disposed) return;
            // "Disconnected" = jedyny stan, w którym wolno wołać start() (inaczej SignalR rzuca).
            if (connection.state !== "Disconnected") return;
            lastAttemptAtRef.current = Date.now();
            try {
                if (isRestart) {
                    // Odśwież tożsamość i token SignalR. Przy wygasłym JWT interceptor po drodze
                    // wykona /users/refresh, więc negocjacja poleci już ze świeżym tokenem.
                    try {
                        const meRes = await apiService.getMe();
                        if (meRes && meRes.data && meRes.data.accessToken) setSignalRToken(meRes.data.accessToken);
                    } catch { /* brak sieci albo nieważna sesja — kolejna próba za chwilę */ }
                    if (disposed) return;
                }
                await connection.start();
                if (disposed) {
                    connection.stop();
                    return;
                }
                restartAttemptRef.current = 0;
                setIsConnected(true);
                connectionRef.current = connection;
                if (isRestart) {
                    // Nadrobienie tego, co przyszło w czasie ciszy.
                    fetchNotifications(1, false);
                    window.dispatchEvent(new Event("realtime-reconnected"));
                }
            } catch (err) {
                setIsConnected(false);
                scheduleRestart();
            }
        };

        // Powrót karty na wierzch / powrót sieci — natychmiastowa próba zamiast czekania,
        // ale nie częściej niż raz na WAKEUP_THROTTLE_MS (przełączanie kart nie robi burzy).
        const wakeUpRealtime = () => {
            if (disposed || connection.state !== "Disconnected") return;
            if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
            if (Date.now() - lastAttemptAtRef.current < WAKEUP_THROTTLE_MS) return;
            // Licznika prób celowo NIE zerujemy: gdyby backend leżał, przełączanie zakładek
            // nie może cofać odstępów do zera. Po udanym połączeniu zeruje go startConnection.
            clearRestartTimer();
            startConnection(true);
        };

        window.addEventListener("online", wakeUpRealtime);
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", wakeUpRealtime);
        }

        startConnection();

        return () => {
            disposed = true;
            clearRestartTimer();
            window.removeEventListener("online", wakeUpRealtime);
            if (typeof document !== "undefined") {
                document.removeEventListener("visibilitychange", wakeUpRealtime);
            }
            connection.stop();
            connectionRef.current = null;
            window.removeEventListener("hr-notification", handleHrNotification);
        };
    }, [user, fetchNotifications, showDesktopNotification]);

    // Wywołanie metody huba (JoinEditing/LeaveEditing itp.) — best-effort; cicho pomija gdy brak połączenia.
    const hubInvoke = useCallback(async (method, ...args) => {
        const conn = connectionRef.current;
        if (!conn || conn.state !== "Connected") return;
        try { await conn.invoke(method, ...args); } catch { /* offline / metoda niedostępna — pomijamy */ }
    }, []);

    const value = useMemo(() => ({
        notifications,
        unreadCount,
        isConnected,
        isOpen,
        hasMore,
        togglePanel,
        closePanel,
        markAsRead,
        // [2026-09-30, B3] Otwarcie obiektu = odczyt wszystkich powiadomień o nim (hook useOdczytObiektu).
        markReadForObject,
        markAllAsRead,
        deleteNotification,
        fetchMore,
        fetchNotifications,
        // [OBSZAR 20] Nieprzeczytane bez zawężenia po dacie — wszystko, co liczy licznik, da się dociągnąć i kliknąć.
        fetchUnread,
        fetchMoreUnread,
        unreadHasMore,
        registerToastCallback,
        registerAnnouncementCallback,
        registerBadgeCallback,
        registerTermsCallback,
        registerAnnouncementReadCallback,
        registerTermsAcceptedCallback,
        // Wywołanie metody huba (np. JoinEditing/LeaveEditing) — best-effort, tylko gdy połączony.
        hubInvoke,
        // Powiadomienia systemowe Windows
        desktopEnabled,
        desktopPermission,
        toggleDesktopNotifications,
        requestDesktopPermission,
    }), [
        notifications, unreadCount, isConnected, isOpen, hasMore,
        togglePanel, closePanel, markAsRead, markReadForObject, markAllAsRead,
        deleteNotification, fetchMore, fetchNotifications,
        fetchUnread, fetchMoreUnread, unreadHasMore,
        registerToastCallback, registerAnnouncementCallback,
        registerBadgeCallback, registerTermsCallback, hubInvoke,
        registerAnnouncementReadCallback, registerTermsAcceptedCallback,
        desktopEnabled, desktopPermission,
        toggleDesktopNotifications, requestDesktopPermission,
    ]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
